import React, { useEffect, useRef, useCallback } from "react";
import { Box } from "@mui/material";
import {
    ExceptionsSidebarView,
    ExceptionsSidebarModel
} from "app/components/flow/designer/app-exceptions/exceptions-sidebar";
import { sxStyles } from "../component-form/sidebar.styles";
import { useAppSelector, useAppDispatch } from "src/stores/redux/hooks";
import {
    getIsExceptionsSidebarOpen,
    getExceptionsCount as getExceptionsCountState,
    getNodes
} from "../../helpers/selectors";
import {
    setIsExceptionsSidebarOpenAction,
    setExceptionsCountAction,
    setSelectedNodeAction,
    setIsRightSidebarOpenAction,
    closeAllSidebarsAction
} from "src/stores/redux/features/flowdesigner/flowdesigner.slice";
import { getExceptionsCount } from "../../helpers/flow.utils";
import App from "app";
import store from "src/stores/redux/store";
import { findNode } from "../../helpers/node.utils";
import { useParams } from "react-router";
import { FlowUrlProps } from "../../flowdesigner";

type ExceptionsSidebarProps = {
    appModel: any;
};

const ExceptionsSidebarWrapper = ({ appModel }: ExceptionsSidebarProps) => {
    const { appName, flowName } = useParams<FlowUrlProps>();
    const isExceptionsSidebarOpen = useAppSelector(getIsExceptionsSidebarOpen);
    const exceptionsCount = useAppSelector(getExceptionsCountState);
    const dispatch = useAppDispatch();
    const handleCloseExceptions = useCallback(() => {
        dispatch(setIsExceptionsSidebarOpenAction(false));
    }, [dispatch]);

    const handleRefreshExceptionCounter = useCallback(() => {
        if (appModel?.id) {
            setTimeout(() => {
                getExceptionsCount(appModel).then(count => {
                    dispatch(setExceptionsCountAction(count));
                });
            }, 3000);
        }
    }, [appModel?.id, dispatch]);
    const containerRef = useRef<HTMLDivElement>(null);
    const sidebarViewRef = useRef<any>(null);

    useEffect(() => {
        const handler = ({ appName, entityName }: { appName: string; entityName: string }) => {
            const nodes = getNodes(store.getState());
            const selectedNode = findNode(nodes, entityName);

            dispatch(closeAllSidebarsAction({ appName, flowName }));
            dispatch(setSelectedNodeAction(selectedNode));
            dispatch(setIsRightSidebarOpenAction(true));
        };
        App.vent.on("exceptions:navigate-to-node", handler);
        return () => App.vent.off("exceptions:navigate-to-node", handler);
    }, [dispatch]);

    useEffect(() => {
        if (!appModel || !containerRef.current) {
            return;
        }

        // Create the ExceptionsSidebarModel
        const exceptionsSidebarModel = new ExceptionsSidebarModel({
            app: appModel,
            hasExceptions: exceptionsCount > 0
        });

        // Create the Marionette ExceptionsSidebarView
        const exceptionsView = new ExceptionsSidebarView({
            model: exceptionsSidebarModel
        });

        // Attach event listeners
        if (isExceptionsSidebarOpen) {
            exceptionsView.on("cancel", handleCloseExceptions);
        }

        // Always listen for refreshcounter event (triggered when exception store is turned on)
        exceptionsView.on("refreshcounter", handleRefreshExceptionCounter);

        // Render the view
        exceptionsView.render();
        containerRef.current.appendChild(exceptionsView.el);

        // Store reference for cleanup
        sidebarViewRef.current = exceptionsView;

        // Cleanup function
        return () => {
            if (sidebarViewRef.current) {
                sidebarViewRef.current.destroy();
                sidebarViewRef.current = null;
            }
        };
    }, [appModel?.id, isExceptionsSidebarOpen]); // Only recreate when app ID changes, not hasExceptions
    if (!appModel || !isExceptionsSidebarOpen) return null;

    return (
        <Box
            ref={containerRef}
            className="sidebar right-sidebar"
            sx={[sxStyles.sidebar("SIDEBAR"), { padding: "0px !important" }]}
        />
    );
};

export default ExceptionsSidebarWrapper;
